import { FC, useState, useEffect, useRef, memo, useCallback } from 'react';
import {
    Card,
    CardContent,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Collapse,
    TextField,
    MenuItem,
    Divider,
    Alert,
    LinearProgress,
    CircularProgress,
} from '@mui/material';
import {
    Settings,
    ExpandMore,
    ExpandLess,
    PhotoLibrary,
    ContentCopy,
    Delete,
    CloudUpload,
    Close,
    Edit,
    ZoomIn,
    Wallpaper,
    Label,
    MoreVert,
    Brush,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { HangerCard as IHangerCard, PhotoFile, AIModelSelectionType } from '../../../../context/MannequinContext';
import { SCUIButton } from '../../../../../../atomic/atoms';
import { StudioPromptSelector } from '../../../../../../pages_studio/components/StudioPromptSelector';
import { PromptType } from '../../../../../../types/StudioPrompt';
import {
    extractBarcodeFromFilename,
    inferOutputFormatFromImageUrl,
    isLcwUpscaleHangerModel,
    resolveHangerCardQualityFallback,
    compressImage,
    resolveHangerCardModelSelection,
    isAzureModelSelectionBlocked,
    isGeminiLikeHangerModel,
    ENABLE_HANGER_CARD_SIZE_INFO,
} from '../../AIGeneration.helper';
import { BackgroundLabelPopover } from '../BackgroundLabelPopover/BackgroundLabelPopover';
import { BackgroundLabelItem } from '../../../../../../services/studio/StudioQuery/BackgroundLabel/BackgroundLabelQuery.services';
import { InpaintingModal } from '../InpaintingModal/InpaintingModal';
import { formatFileSize, getBase64Size } from '../../../../../../utils/fileSizeFormatter';
import { HangerCardSizeInfo } from './HangerCardSizeInfo';
import { HangerCardOutputConfigPopover } from './HangerCardOutputConfigPopover';

interface HangerCardProps {
    card: IHangerCard;
    index: number;
    isDragging: boolean;
    dragOverId: string | null;
    dragPosition: 'before' | 'after' | null;
    loadingGif: string;
    updateHangerCard: (
        cardId: string,
        updates: Partial<IHangerCard> | ((prevCard: IHangerCard) => Partial<IHangerCard>)
    ) => void;
    applyBackgroundOrLabelToCard: (
        cardId: string,
        url: string,
        name: string,
        type: 'background' | 'label',
        s3Key?: string
    ) => Promise<void>;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragEnd: () => void;
    onDrop: (e: React.DragEvent) => void;
    onGenerate: (id: string) => void;
    onDuplicate: (id: string) => void;
    onDuplicateWithPhotos: (id: string) => void;
    onRemove: (id: string) => void;
    onPreviewImage: (url: string) => void;
    onDownload: (id: string, url: string) => void;
    downloadingCardId: string | null;
    onError?: (msg: string) => void;
    onInpaint: (
        cardId: string,
        maskBase64: string,
        prompt: string,
        referenceBase64: string,
        referenceMimeType: string
    ) => void;
    inpaintingCardId: string | null;
    isTourTarget?: boolean;
    tourActive?: boolean;
    hangerCards: IHangerCard[];
}

const MAX_PHOTOS_PER_CARD = 10;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_IMAGE_WIDTH = 2048;
const MAX_IMAGE_HEIGHT = 2048;
const COMPRESSION_QUALITY = 1.0;

const DEBOUNCE_MS = 300;

const HangerCardInner: FC<HangerCardProps> = ({
    card,
    index,
    isDragging,
    dragOverId,
    dragPosition,
    loadingGif,
    updateHangerCard,
    applyBackgroundOrLabelToCard,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDrop,
    onGenerate,
    onDuplicate,
    onDuplicateWithPhotos,
    onRemove,
    onPreviewImage,
    onDownload,
    downloadingCardId,
    onError = () => {},
    onInpaint,
    inpaintingCardId,
    isTourTarget = false,
    tourActive = false,
    hangerCards,
}) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(true);

    // Background/Label Modal State
    const [bgPopoverOpen, setBgPopoverOpen] = useState(false);
    const [labelPopoverOpen, setLabelPopoverOpen] = useState(false);
    const [showExternalMenu, setShowExternalMenu] = useState(false);

    // Photo Drag State
    const [draggedPhotoIndex, setDraggedPhotoIndex] = useState<number | null>(null);
    const [dragOverPhotoIndex, setDragOverPhotoIndex] = useState<number | null>(null);

    // Inpainting State
    const [inpaintingOpen, setInpaintingOpen] = useState(false);

    // Local state for text inputs to avoid re-rendering all cards on every keystroke
    const [localBarcode, setLocalBarcode] = useState(card.config.barcode);
    const [localPrompt, setLocalPrompt] = useState(card.config.prompt);
    const debounceBarcodeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const debouncePromptRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingBarcodeRef = useRef<string | null>(null);
    const pendingPromptRef = useRef<string | null>(null);

    useEffect(() => {
        setLocalBarcode(card.config.barcode);
    }, [card.config.barcode]);
    useEffect(() => {
        setLocalPrompt(card.config.prompt);
    }, [card.config.prompt]);

    useEffect(() => {
        if (tourActive && isTourTarget) {
            setExpanded(true);
        }
    }, [tourActive, isTourTarget]);

    // -- Local Handlers --

    const handleConfigChange = useCallback(
        (field: string, value: any) => {
            updateHangerCard(card.id, (prev) => ({
                config: { ...prev.config, [field]: value },
                errorMessage: undefined,
            }));
        },
        [card.id, updateHangerCard]
    );

    const handleSizeSelectionChange = useCallback(
        (sizeCode: string, measurementText: string) => {
            updateHangerCard(card.id, (prev) => ({
                config: {
                    ...prev.config,
                    selectedSizeCode: sizeCode,
                    sizeMeasurementText: measurementText,
                },
                errorMessage: undefined,
            }));
        },
        [card.id, updateHangerCard]
    );

    const handleSizeInfoDataChange = useCallback(
        (
            sizes: NonNullable<typeof card.config.sizeInfoSizes>,
            productAttributes: NonNullable<typeof card.config.sizeInfoProductAttributes>,
            loaded: boolean
        ) => {
            updateHangerCard(card.id, (prev) => ({
                config: {
                    ...prev.config,
                    sizeInfoSizes: sizes,
                    sizeInfoProductAttributes: productAttributes,
                    sizeInfoLoaded: loaded,
                },
            }));
        },
        [card.id, updateHangerCard]
    );

    useEffect(() => {
        if (isLcwUpscaleHangerModel(card.selectedModel) && card.config.resolution !== '4K') {
            handleConfigChange('resolution', '4K');
        }
    }, [card.selectedModel, card.config.resolution, handleConfigChange]);

    const handleBarcodeChange = useCallback(
        (value: string) => {
            setLocalBarcode(value);
            pendingBarcodeRef.current = value;
            if (debounceBarcodeRef.current) clearTimeout(debounceBarcodeRef.current);
            debounceBarcodeRef.current = setTimeout(() => {
                pendingBarcodeRef.current = null;
                updateHangerCard(card.id, (prev) => ({
                    config: { ...prev.config, barcode: value },
                    errorMessage: undefined,
                }));
            }, DEBOUNCE_MS);
        },
        [card.id, updateHangerCard]
    );

    const handlePromptChange = useCallback(
        (value: string) => {
            setLocalPrompt(value);
            pendingPromptRef.current = value;
            if (debouncePromptRef.current) clearTimeout(debouncePromptRef.current);
            debouncePromptRef.current = setTimeout(() => {
                pendingPromptRef.current = null;
                updateHangerCard(card.id, (prev) => ({
                    config: { ...prev.config, prompt: value },
                    errorMessage: undefined,
                }));
            }, DEBOUNCE_MS);
        },
        [card.id, updateHangerCard]
    );

    // Flush pending debounced values on unmount
    useEffect(() => {
        return () => {
            if (debounceBarcodeRef.current) {
                clearTimeout(debounceBarcodeRef.current);
                if (pendingBarcodeRef.current !== null) {
                    const val = pendingBarcodeRef.current;
                    updateHangerCard(card.id, (prev) => ({
                        config: { ...prev.config, barcode: val },
                    }));
                }
            }
            if (debouncePromptRef.current) {
                clearTimeout(debouncePromptRef.current);
                if (pendingPromptRef.current !== null) {
                    const val = pendingPromptRef.current;
                    updateHangerCard(card.id, (prev) => ({
                        config: { ...prev.config, prompt: val },
                    }));
                }
            }
        };
    }, [card.id, updateHangerCard]);

    // Calculate total size of photos
    const totalPhotosSize = card.photos.reduce((total, photo) => {
        const size = photo.size || photo.file?.size || getBase64Size(photo.preview);
        return total + (size || 0);
    }, 0);
    const formattedTotalSize = formatFileSize(totalPhotosSize);

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, insertIndex?: number) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (card.photos.length >= MAX_PHOTOS_PER_CARD && insertIndex === undefined) {
            onError?.(t('mannequin.aiGeneration.hangerFlow.uploadGenerate.maxPhotosPerCard'));
            return;
        }

        if (!ALLOWED_TYPES.includes(file.type)) {
            onError?.(t('mannequin.aiGeneration.errors.invalidFileType'));
            return;
        }

        try {
            const compressedDataUrl = await compressImage(file, MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT, COMPRESSION_QUALITY);
            const newPhoto: PhotoFile = {
                id: crypto.randomUUID(),
                file: file,
                preview: compressedDataUrl,
                url: '',
                type: 'product',
                name: file.name,
                size: file.size,
            };

            updateHangerCard(card.id, (prev) => {
                let updatedPhotos = [...prev.photos];
                if (insertIndex !== undefined) {
                    updatedPhotos[insertIndex] = newPhoto;
                } else {
                    updatedPhotos.push(newPhoto);
                }

                const result: Partial<IHangerCard> = { photos: updatedPhotos };

                if (insertIndex === 0 || (insertIndex === undefined && prev.photos.length === 0)) {
                    const barcode = extractBarcodeFromFilename(file.name);
                    if (barcode) {
                        result.config = { ...prev.config, barcode };
                        setLocalBarcode(barcode);
                    }
                }
                return result;
            });
            onError?.('');
        } catch {
            onError?.(t('mannequin.aiGeneration.errors.compressionFailed'));
        }
    };

    const handlePhotoDrop = (targetIndex: number) => {
        if (draggedPhotoIndex === null) return;

        updateHangerCard(card.id, (prev) => {
            const newPhotos = [...prev.photos];
            const [moved] = newPhotos.splice(draggedPhotoIndex, 1);
            newPhotos.splice(targetIndex, 0, moved);

            const result: Partial<IHangerCard> = { photos: newPhotos };
            if ((draggedPhotoIndex === 0 || targetIndex === 0) && newPhotos[0]) {
                const photoName = newPhotos[0].name || newPhotos[0].file?.name || '';
                const barcode = extractBarcodeFromFilename(photoName);
                if (barcode) {
                    result.config = { ...prev.config, barcode };
                    setLocalBarcode(barcode);
                }
            }
            return result;
        });
        setDraggedPhotoIndex(null);
        setDragOverPhotoIndex(null);
    };

    const handlePhotoDragEnd = () => {
        setDraggedPhotoIndex(null);
        setDragOverPhotoIndex(null);
    };

    const handleCardFileDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);

        // If no files, let it bubble to card drop handler
        if (files.length === 0) return;

        // Stop propagation only if we are handling files
        e.stopPropagation();

        if (files.length + card.photos.length > MAX_PHOTOS_PER_CARD) {
            onError?.(t('mannequin.aiGeneration.hangerFlow.uploadGenerate.maxPhotosPerCard'));
            return;
        }

        const newPhotos: PhotoFile[] = [];
        for (const file of files) {
            if (!ALLOWED_TYPES.includes(file.type)) continue;
            try {
                const compressed = await compressImage(file, MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT, COMPRESSION_QUALITY);
                newPhotos.push({
                    id: crypto.randomUUID(),
                    file,
                    preview: compressed,
                    url: '',
                    type: 'product',
                    name: file.name,
                    size: file.size,
                });
            } catch (error) {
                console.error(error);
            }
        }

        if (newPhotos.length > 0) {
            updateHangerCard(card.id, (prev) => {
                const result: Partial<IHangerCard> = { photos: [...prev.photos, ...newPhotos] };
                if (prev.photos.length === 0 && newPhotos[0].file) {
                    const barcode = extractBarcodeFromFilename(newPhotos[0].file.name);
                    if (barcode) {
                        result.config = { ...prev.config, barcode };
                        setLocalBarcode(barcode);
                    }
                }
                return result;
            });
        }
    };

    // -- Background / Label selection handler --
    const handleBackgroundSelect = async (item: BackgroundLabelItem) => {
        setBgPopoverOpen(false);
        setShowExternalMenu(false);
        await applyBackgroundOrLabelToCard(card.id, item.s3Url, item.name, 'background', item.s3Key);
    };

    const handleLabelSelect = async (item: BackgroundLabelItem) => {
        setLabelPopoverOpen(false);
        setShowExternalMenu(false);
        await applyBackgroundOrLabelToCard(card.id, item.s3Url, item.name, 'label', item.s3Key);
    };

    // -- Options --
    const aiModelOptions = [
        {
            value: 'GoogleGemini' as AIModelSelectionType,
            label: t('mannequin.aiGeneration.configuration.googleGemini'),
        },
        {
            value: 'GoogleGeminiFlash' as AIModelSelectionType,
            label: t('mannequin.aiGeneration.configuration.googleGeminiFlash'),
        },
        {
            value: 'GoogleGeminiNanoBanana21' as AIModelSelectionType,
            label: t('mannequin.aiGeneration.configuration.googleGeminiNanoBanana21'),
        },
        {
            value: 'GoogleGeminiNanoBanana21Lcw' as AIModelSelectionType,
            label: t('mannequin.aiGeneration.configuration.googleGeminiNanoBanana21Lcw'),
        },
        {
            value: 'GeminiProLcw' as AIModelSelectionType,
            label: t('mannequin.aiGeneration.configuration.geminiProLcw'),
        },
        {
            value: 'AzureOpenAIGpt' as AIModelSelectionType,
            label: t('mannequin.aiGeneration.configuration.azureOpenAIGpt', 'Azure GPT Image'),
        },
        {
            value: 'AIGate' as AIModelSelectionType,
            label: t('mannequin.aiGeneration.configuration.aiGate', 'AIGate GPT Image'),
        },
        {
            value: 'AIGateProLcw' as AIModelSelectionType,
            label: t('mannequin.aiGeneration.configuration.aiGateProLcw', 'AIGate Pro LCW'),
        },
    ];

    const handleModelChange = (requestedModel: AIModelSelectionType) => {
        const { selectedModel, config, azureLimitApplied } = resolveHangerCardModelSelection({
            cards: hangerCards,
            intent: 'change',
            requestedModel,
            cardId: card.id,
            config: card.config,
        });

        updateHangerCard(card.id, () => ({
            selectedModel,
            config,
            errorMessage: azureLimitApplied ? t('mannequin.aiGeneration.configuration.maxAzureCardsLimit') : undefined,
        }));
    };

    return (
        <Card
            data-hanger-card-id={card.id}
            className={`hanger-card ${showExternalMenu ? 'hanger-card--external-tabs' : ''} ${isTourTarget ? 'tour-target-card' : ''} ${card.generationStatus === 'loading' ? 'hanger-card--loading' : ''} ${card.generationStatus === 'success' ? 'hanger-card--success' : ''} ${card.generationStatus === 'error' ? 'hanger-card--error' : ''} ${card.generationStatus === 'interrupted' ? 'hanger-card--interrupted' : ''}`}
            elevation={isDragging ? 8 : 4}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDrop={onDrop}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                transform: isDragging ? 'scale(1.02) translateY(-4px)' : 'scale(1)',
                boxShadow: isDragging ? '0 12px 24px rgba(0,0,0,0.2)' : undefined,
                opacity: isDragging ? 0.9 : 1,
                zIndex: isDragging ? 100 : 1,
            }}
        >
            {/* External Tab Menu - Background & Label (shown only when toggled from header) */}
            {showExternalMenu && (
                <Box
                    className="hanger-card__external-tabs"
                    sx={{
                        position: 'absolute',
                        right: -36,
                        top: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        zIndex: 10,
                    }}
                >
                    <Tooltip title={t('mannequin.aiGeneration.popover.selectBackground')} placement="right">
                        <IconButton
                            size="small"
                            onClick={() => {
                                setBgPopoverOpen(!bgPopoverOpen);
                                setLabelPopoverOpen(false);
                            }}
                            sx={{
                                backgroundColor: card.config.backgroundUrl ? 'primary.main' : 'background.paper',
                                color: card.config.backgroundUrl ? 'primary.contrastText' : 'text.secondary',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                borderRadius: '0 8px 8px 0',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                    backgroundColor: card.config.backgroundUrl ? 'primary.dark' : 'action.hover',
                                    transform: 'translateX(2px)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            <Wallpaper sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('mannequin.aiGeneration.popover.selectLabel')} placement="right">
                        <IconButton
                            size="small"
                            onClick={() => {
                                setLabelPopoverOpen(!labelPopoverOpen);
                                setBgPopoverOpen(false);
                            }}
                            sx={{
                                backgroundColor: card.config.labelUrl ? 'secondary.main' : 'background.paper',
                                color: card.config.labelUrl ? 'secondary.contrastText' : 'text.secondary',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                borderRadius: '0 8px 8px 0',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                    backgroundColor: card.config.labelUrl ? 'secondary.dark' : 'action.hover',
                                    transform: 'translateX(2px)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            <Label sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Background/Label Modals */}
            <BackgroundLabelPopover
                open={bgPopoverOpen}
                onClose={() => setBgPopoverOpen(false)}
                type="background"
                onSelect={handleBackgroundSelect}
            />
            <BackgroundLabelPopover
                open={labelPopoverOpen}
                onClose={() => setLabelPopoverOpen(false)}
                type="label"
                onSelect={handleLabelSelect}
            />

            {/* Drop Indicators */}
            {dragOverId === card.id && (
                <Box
                    sx={{
                        position: 'absolute',
                        [dragPosition === 'before' ? 'left' : 'right']: 0,
                        top: 0,
                        bottom: 0,
                        width: '8px',
                        zIndex: 200,
                        backgroundColor: '#1565c0',
                        pointerEvents: 'none',
                    }}
                />
            )}

            {/* Header - Drag handle */}
            <Box
                className={`hanger-card__status-bar ${isTourTarget ? 'tour-status-bar' : ''}`}
                draggable={card.generationStatus !== 'loading'}
                onDragStart={onDragStart}
                sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' }, flexWrap: 'wrap', gap: 1 }}
            >
                <Box className="card-header-badge">
                    <Typography className="card-header-badge__text" variant="caption">
                        {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.cardTitle', { number: index + 1 })}
                    </Typography>
                </Box>

                <Box sx={{ flex: 1 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={t('mannequin.aiGeneration.hangerCard.backgroundMenu')}>
                        <IconButton
                            size="small"
                            className={isTourTarget ? 'tour-card-menu' : undefined}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowExternalMenu(!showExternalMenu);
                                if (showExternalMenu) {
                                    setBgPopoverOpen(false);
                                    setLabelPopoverOpen(false);
                                }
                            }}
                            sx={{
                                color:
                                    showExternalMenu || card.config.backgroundUrl || card.config.labelUrl
                                        ? 'primary.main'
                                        : 'text.secondary',
                            }}
                        >
                            <MoreVert fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.duplicateWithPhotos')}>
                        <IconButton
                            size="small"
                            color="secondary"
                            className={isTourTarget ? 'tour-duplicate-with-photos' : undefined}
                            onClick={() => onDuplicateWithPhotos(card.id)}
                        >
                            <PhotoLibrary fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('general.duplicate')}>
                        <IconButton
                            size="small"
                            color="primary"
                            className={isTourTarget ? 'tour-duplicate-card' : undefined}
                            onClick={() => onDuplicate(card.id)}
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <IconButton size="small" color="error" onClick={() => onRemove(card.id)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {card.generationStatus === 'loading' && (
                <LinearProgress variant="determinate" value={card.progress || 0} sx={{ height: 3 }} />
            )}

            <CardContent
                className="hanger-card__content"
                onDrop={card.generationStatus !== 'loading' ? handleCardFileDrop : undefined}
                onDragOver={(e) => {
                    e.preventDefault();
                }}
            >
                {/* Config Section */}
                <Box className={`hanger-card__config-section ${isTourTarget ? 'tour-config-section' : ''}`}>
                    <Box
                        className="config-header"
                        onClick={() => setExpanded(!expanded)}
                        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                        <Settings sx={{ mr: 1 }} />
                        <Typography variant="subtitle2" sx={{ flex: 1 }}>
                            {t('mannequin.aiGeneration.configuration.title')}
                        </Typography>
                        <IconButton size="small">{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
                    </Box>
                    <Collapse in={expanded}>
                        <Box className="config-form">
                            <TextField
                                fullWidth
                                select
                                size="small"
                                label={t('mannequin.aiGeneration.configuration.aiModel')}
                                value={card.selectedModel}
                                onChange={(e) => handleModelChange(e.target.value as AIModelSelectionType)}
                                sx={{ mb: 2 }}
                            >
                                {aiModelOptions.map((opt) => (
                                    <MenuItem
                                        key={opt.value}
                                        value={opt.value}
                                        disabled={
                                            opt.value === 'AzureOpenAIGpt' &&
                                            isAzureModelSelectionBlocked(hangerCards, card.id)
                                        }
                                    >
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                fullWidth
                                required
                                size="small"
                                label={t('mannequin.aiGeneration.configuration.barcode')}
                                value={localBarcode}
                                onChange={(e) => handleBarcodeChange(e.target.value)}
                                sx={{ mb: 2 }}
                            />

                            <HangerCardOutputConfigPopover
                                selectedModel={card.selectedModel}
                                resolution={card.config.resolution}
                                aspectRatio={card.config.aspectRatio}
                                outputFormat={card.config.outputFormat || 'jpeg'}
                                generatedOutputFormat={
                                    card.generatedResults[0]?.outputFormat ??
                                    inferOutputFormatFromImageUrl(card.generatedResults[0]?.generatedUrls[0])
                                }
                                quality={card.config.quality || resolveHangerCardQualityFallback(card.selectedModel)}
                                outputFormatDisabled={card.generationStatus === 'loading'}
                                onResolutionChange={(value) => handleConfigChange('resolution', value)}
                                onAspectRatioChange={(value) => handleConfigChange('aspectRatio', value)}
                                onOutputFormatChange={(value) => handleConfigChange('outputFormat', value)}
                                onQualityChange={(value) => handleConfigChange('quality', value)}
                            />

                            {/* Prompt Selector */}
                            <Box className={isTourTarget ? 'tour-prompt-editor' : undefined} sx={{ mb: 2 }}>
                                <StudioPromptSelector
                                    promptText={localPrompt}
                                    onPromptTextChange={handlePromptChange}
                                    promptType={PromptType.AskilıSecim}
                                    label={t('mannequin.aiGeneration.configuration.prompt')}
                                    required
                                />
                            </Box>

                            {ENABLE_HANGER_CARD_SIZE_INFO && (
                                <HangerCardSizeInfo
                                    barcode={card.config.barcode}
                                    selectedSizeCode={card.config.selectedSizeCode}
                                    sizeMeasurementText={card.config.sizeMeasurementText}
                                    sizeInfoSizes={card.config.sizeInfoSizes}
                                    sizeInfoProductAttributes={card.config.sizeInfoProductAttributes}
                                    sizeInfoLoaded={card.config.sizeInfoLoaded}
                                    onSizeSelectionChange={handleSizeSelectionChange}
                                    onSizeInfoDataChange={handleSizeInfoDataChange}
                                    onSizeMeasurementTextChange={(text) =>
                                        handleConfigChange('sizeMeasurementText', text)
                                    }
                                />
                            )}
                        </Box>
                    </Collapse>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Error Alert */}
                {card.errorMessage &&
                    !card.errorMessage.includes('barcode') &&
                    !card.errorMessage.includes('prompt') && (
                        <Alert
                            severity={card.generationStatus === 'interrupted' ? 'warning' : 'error'}
                            sx={{ mb: 2 }}
                            onClose={() => updateHangerCard(card.id, { errorMessage: undefined })}
                        >
                            {card.errorMessage}
                        </Alert>
                    )}

                {/* Photos Grid */}
                <Box className={`hanger-card__photos-grid ${isTourTarget ? 'tour-photos-grid' : ''}`}>
                    {Array.from({ length: MAX_PHOTOS_PER_CARD }).map((_, pIndex) => {
                        const photo = card.photos[pIndex];
                        return (
                            <Box
                                key={pIndex}
                                className={`photo-slot ${photo ? 'filled' : 'empty'} ${dragOverPhotoIndex === pIndex ? 'drop-target' : ''}`}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    if (draggedPhotoIndex !== null) {
                                        setDragOverPhotoIndex(pIndex);
                                    }
                                }}
                                onDragLeave={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const currentTarget = e.currentTarget;
                                    const relatedTarget = e.relatedTarget as Node;
                                    if (currentTarget.contains(relatedTarget)) return;

                                    setDragOverPhotoIndex(null);
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    if (draggedPhotoIndex !== null) {
                                        handlePhotoDrop(pIndex);
                                    }
                                }}
                            >
                                {photo ? (
                                    <Box
                                        draggable={card.generationStatus !== 'loading'}
                                        onDragStart={(e) => {
                                            e.stopPropagation();
                                            setDraggedPhotoIndex(pIndex);
                                        }}
                                        onDragEnd={handlePhotoDragEnd}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            position: 'relative',
                                            '&:hover .photo-size-badge': {
                                                opacity: 0,
                                            },
                                        }}
                                    >
                                        <img
                                            src={photo.preview}
                                            alt=""
                                            className="photo-slot__image"
                                            onClick={() =>
                                                pIndex === 0 &&
                                                handleFileSelect({ target: { files: [photo.file] } } as any, 0)
                                            }
                                        />
                                        <Box
                                            className="photo-size-badge"
                                            sx={{
                                                position: 'absolute',
                                                bottom: 4,
                                                right: 4,
                                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                color: 'white',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                fontSize: '0.65rem',
                                                fontWeight: 'bold',
                                                zIndex: 2,
                                                pointerEvents: 'none',
                                                transition: 'opacity 0.2s ease',
                                                opacity: 1,
                                            }}
                                        >
                                            {formatFileSize(
                                                photo.size || photo.file?.size || getBase64Size(photo.preview)
                                            )}
                                        </Box>
                                        <Box className="photo-slot__overlay">
                                            <input
                                                type="file"
                                                hidden
                                                id={`edit-${card.id}-${pIndex}`}
                                                onChange={(e) => handleFileSelect(e, pIndex)}
                                            />
                                            <IconButton
                                                className="photo-slot__btn photo-slot__btn--zoom"
                                                onClick={() => onPreviewImage(photo.preview)}
                                            >
                                                <ZoomIn fontSize="inherit" />
                                            </IconButton>
                                            <IconButton
                                                className="photo-slot__btn photo-slot__btn--edit"
                                                onClick={() =>
                                                    document.getElementById(`edit-${card.id}-${pIndex}`)?.click()
                                                }
                                            >
                                                <Edit fontSize="inherit" />
                                            </IconButton>
                                            <IconButton
                                                className="photo-slot__btn photo-slot__btn--delete"
                                                onClick={() =>
                                                    updateHangerCard(card.id, (prev) => {
                                                        const deletedIndex = prev.photos.findIndex((p) => p.id === photo.id);
                                                        const updatedPhotos = prev.photos.filter((p) => p.id !== photo.id);
                                                        const result: Partial<IHangerCard> = { photos: updatedPhotos };

                                                        // İlk foto silindiğinde barkodu yeni ilk fotonun dosya adına göre güncelle
                                                        if (deletedIndex === 0 && updatedPhotos[0]) {
                                                            const photoName =
                                                                updatedPhotos[0].name || updatedPhotos[0].file?.name || '';
                                                            const barcode = extractBarcodeFromFilename(photoName);
                                                            if (barcode) {
                                                                result.config = { ...prev.config, barcode };
                                                                setLocalBarcode(barcode);
                                                            }
                                                        }
                                                        return result;
                                                    })
                                                }
                                            >
                                                <Close fontSize="inherit" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ) : (
                                    <>
                                        <input
                                            type="file"
                                            hidden
                                            id={`upload-${card.id}-${pIndex}`}
                                            onChange={(e) => handleFileSelect(e)}
                                        />
                                        <label htmlFor={`upload-${card.id}-${pIndex}`} className="photo-slot__label">
                                            <Box className="photo-slot__placeholder">
                                                <CloudUpload className="placeholder-icon" />
                                                <Typography className="placeholder-text" variant="caption">
                                                    #{pIndex + 1}
                                                </Typography>
                                            </Box>
                                        </label>
                                    </>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* Actions */}
                <Box className="hanger-card__actions">
                    <Box className="hanger-card__actions-buttons">
                        <SCUIButton
                            type={
                                card.generationStatus === 'success'
                                    ? 'secondary'
                                    : card.generationStatus === 'interrupted'
                                      ? 'primary'
                                      : 'primary'
                            }
                            size="m"
                            sizeType="default"
                            className={isTourTarget ? 'tour-generate-btn' : undefined}
                            text={
                                card.generationStatus === 'loading'
                                    ? t('mannequin.aiGeneration.hangerFlow.uploadGenerate.generating')
                                    : card.generationStatus === 'interrupted'
                                      ? t('mannequin.aiGeneration.hangerFlow.uploadGenerate.regenerate')
                                      : t('mannequin.aiGeneration.hangerFlow.uploadGenerate.generateCard')
                            }
                            onClick={() => onGenerate(card.id)}
                            disabled={card.generationStatus === 'loading'}
                        />
                        {card.generationStatus === 'success' && card.generatedResults.length > 0 && (
                            <SCUIButton
                                type="primary"
                                size="m"
                                sizeType="default"
                                text={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.download')}
                                onClick={() => onDownload(card.id, card.generatedResults[0].generatedUrls[0])}
                                disabled={downloadingCardId === card.id}
                            />
                        )}
                    </Box>
                    {card.photos.length > 0 && (
                        <Typography variant="body2" color="text.secondary" className="hanger-card__actions-total">
                            {t('mannequin.aiGeneration.hangerCard.totalSize', { size: formattedTotalSize })}
                        </Typography>
                    )}
                </Box>

                {/* Results */}
                {((card.generationStatus === 'success' && card.generatedResults.length > 0) ||
                    card.generationStatus === 'loading') && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box
                            className={`hanger-card__generated-section ${isTourTarget ? 'tour-generated-section' : ''}`}
                        >
                            {card.generationStatus === 'loading' ? (
                                <Box className="generated-photo loading-placeholder">
                                    {loadingGif ? (
                                        <img
                                            src={loadingGif}
                                            alt="Loading"
                                            style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <CircularProgress size={48} />
                                    )}
                                </Box>
                            ) : (
                                <Box
                                    className="generated-photo"
                                    data-hanger-card-generated-image
                                    sx={{ position: 'relative' }}
                                >
                                    <img
                                        src={card.generatedResults[0].generatedUrls[0]}
                                        alt="Result"
                                        onClick={() => onPreviewImage(card.generatedResults[0].generatedUrls[0])}
                                        style={{ width: '100%', borderRadius: '8px', cursor: 'pointer' }}
                                    />
                                    {/* Inpainting Button Overlay */}
                                    {isGeminiLikeHangerModel(card.selectedModel) && (
                                        <Tooltip
                                            title={t(
                                                'mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.editArea'
                                            )}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setInpaintingOpen(true);
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    bgcolor: 'rgba(144, 202, 249, 0.9)',
                                                    color: '#1a1a2e',
                                                    '&:hover': {
                                                        bgcolor: '#90caf9',
                                                        transform: 'scale(1.1)',
                                                    },
                                                    transition: 'all 0.2s ease',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                                }}
                                            >
                                                <Brush fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </>
                )}

                {tourActive &&
                    isTourTarget &&
                    card.generationStatus !== 'success' &&
                    card.generationStatus !== 'loading' && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box
                                className="hanger-card__generated-section tour-generated-section"
                                sx={{
                                    minHeight: 80,
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0.6,
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    {t('mannequin.aiGeneration.tour.generatedSection.placeholder')}
                                </Typography>
                            </Box>
                        </>
                    )}

                {/* Inpainting Modal */}
                {card.generationStatus === 'success' && card.generatedResults.length > 0 && (
                    <InpaintingModal
                        open={inpaintingOpen}
                        imageUrl={card.generatedResults[0].generatedUrls[0]}
                        onClose={() => setInpaintingOpen(false)}
                        onGenerate={(maskBase64, prompt, referenceImage) => {
                            onInpaint(card.id, maskBase64, prompt, referenceImage.base64, referenceImage.mimeType);
                            setInpaintingOpen(false);
                        }}
                        isLoading={inpaintingCardId === card.id}
                    />
                )}
            </CardContent>
        </Card>
    );
};

export const HangerCard = memo(HangerCardInner, (prevProps, nextProps) => {
    if (prevProps.card !== nextProps.card) return false;
    if (prevProps.hangerCards !== nextProps.hangerCards) return false;
    if (prevProps.isDragging !== nextProps.isDragging) return false;
    if (prevProps.downloadingCardId !== nextProps.downloadingCardId) return false;
    if (prevProps.inpaintingCardId !== nextProps.inpaintingCardId) return false;
    if (prevProps.loadingGif !== nextProps.loadingGif) return false;
    if (prevProps.isTourTarget !== nextProps.isTourTarget) return false;
    if (prevProps.tourActive !== nextProps.tourActive) return false;
    // Only re-render for drag indicators if this card is involved
    if (prevProps.dragOverId !== nextProps.dragOverId) {
        if (prevProps.dragOverId === prevProps.card.id || nextProps.dragOverId === nextProps.card.id) return false;
    }
    if (prevProps.dragPosition !== nextProps.dragPosition && nextProps.dragOverId === nextProps.card.id) return false;
    return true;
});
